import React, { useEffect, useRef, useState } from "react";
import { useSwipeable } from "react-swipeable";

import Icon from "../assets/globe.svg";
import Arrow from "../assets/arrow.svg";
import LeftSlider from "../assets/left_slider.svg";
import RightSlider from "../assets/right_slider.svg";
import AppFrame from "../assets/App-frame.svg";
import Appinside from "../assets/App-inside.svg";
import AiInside from "../assets/Ai-inside.svg";
import AiFrame from "../assets/Ai-frame.svg";
import SeoFrame from "../assets/Seo_frame.svg";
import SeoInside from "../assets/Seo_inside.svg";

const cards = [
  {
    name: "Web Development",
    number: "01",
    icon: Icon,
    subicon: Arrow,
    description:
      "We build modern, responsive, and scalable websites tailored to your business needs, ensuring a seamless user experience and strong online presence.",
  },
  {
    name: "Mobile Development",
    number: "02",
    icon: AppFrame,
    subicon: Appinside,
    description:
      "Boosting online visibility and search rankings with data-driven strategies, ensuring higher traffic and better audience engagement.",
  },
  {
    name: "AI Integration",
    number: "03",
    icon: AiFrame,
    subicon: AiInside,
    description:
      "Enhancing customer engagement and operational efficiency with intelligent automation and conversational AI solutions.",
  },
  {
    name: "Trainings",
    number: "04",
    icon: Icon,
    subicon: Arrow,
    description:
      "Equipping freshers and professionals with industry-relevant skills through hands-on learning and real-world projects.",
  },
  {
    name: "SEO",
    number: "05",
    icon: SeoFrame,
    subicon: SeoInside,
    description:
      "Boosting online visibility and search rankings with data-driven strategies, ensuring higher traffic and better audience engagement.",
  },
];

const getCardsPerView = () => {
  const width = window.innerWidth;
  if (width >= 1024) return 3;
  if (width >= 640) return 2;
  return 1;
};

// 👇 Custom Card Component
const Card = ({
  name,
  number,
  icon,
  subicon,
  description,
  index,
  cardsPerView,
  startIndex,
  setIsHovered,
}) => {
  const isActive = index === startIndex;

  return (
    <div
      className={`transition-all duration-500 ease-in-out transform 
      ${isActive ? "scale-100 opacity-100 z-10" : "scale-[0.92] opacity-100"} 
      shrink-0 w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.3333%-16px)] 
      h-[28rem] bg-[#FFFFFF0A] p-[1rem] flex justify-between flex-col px-6 
      border-[#AF70CF] border-[1px] backdrop-blur 
      hover:bg-[#4603AB] hover:scale-[1.05] hover:border-[linear-gradient(180deg,_rgba(83,64,181,1)_20%,_rgba(27,7,132,1)_100%)]
      rounded-xl shadow-md`}
      onTouchStart={() => setIsHovered(true)}
      onTouchEnd={() => setIsHovered(false)}
    >
      <div className="rubik-medium text-[30px] text-[#6250FF] tracking-wide">
        <p>{number}</p>
      </div>
      <div className="w-full h-1/2 flex justify-center items-center">
        <div className="w-[5rem] h-[5rem] relative group">
          <img
            src={icon}
            alt={name}
            className={`h-[5rem] w-[5rem] transition-transform duration-300 ease-in-out ${
              number === "03" ? "scale-[1.4] group-hover:scale-[1.6]" : ""
            }`}
          />
          <img
            className={`absolute transition-transform duration-300 ease-in-out ${
              number === "01"
                ? "w-[2rem] h-[2rem] right-0 bottom-0 group-hover:scale-[1.8] origin-top-left"
                : number === "02"
                ? "w-[2rem] h-[2rem] right-0 bottom-6 group-hover:scale-[1.8]"
                : number === "03"
                ? "w-[0.5rem] h-[0.5rem] left-[30px] top-[33px] group-hover:scale-[1.4] origin-bottom-right"
                : number === "04"
                ? "w-[2rem] h-[2rem] right-0 bottom-0 group-hover:scale-[1.8] origin-top-left"
                : number === "05"
                ? "w-[2rem] h-[2rem] left-[17px] -top-3 origin-bottom group-hover:scale-[1.6]"
                : ""
            }`}
            src={subicon}
            alt={subicon}
          />
        </div>
      </div>
      <div className="poppins-semibold mt-4 text-[16px] tracking-wide text-white">
        <p>{name}</p>
      </div>
      <div className="poppins-regular text-[12px] mt-2 leading-[18px] text-[#CECECE] text-justify flex">
        <p>{description}</p>
      </div>
    </div>
  );
};

// 👇 Main Component
const Service = ({ screenInfo }) => {
  const { isDesktopModeOnMobile, width } = screenInfo;
  const containerRef = useRef(null);
  const [cardsPerView, setCardsPerView] = useState(getCardsPerView());
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const cardGap = 24;
  const cardWidth = useRef(0);

  useEffect(() => {
    const handleResize = () => {
      const newCardsPerView = getCardsPerView();
      setCardsPerView(newCardsPerView);
      setCurrentIndex(0);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (containerRef.current?.children[0]) {
      cardWidth.current =
        containerRef.current.children[0].offsetWidth + cardGap;
    }
  }, [cardsPerView]);

  //const maxIndex = Math.max(cards.length - cardsPerView, 0);
  const maxIndex = cards.length - 1;

  const handleNext = () => {
    setCurrentIndex((prev) => Math.min(prev + 1, maxIndex));
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  };

  const swipeHandlers = useSwipeable({
    onSwipedLeft: handleNext,
    onSwipedRight: handlePrev,
    trackMouse: true,
  });

  const totalScrollable =
    cardWidth.current * cards.length - cardWidth.current * cardsPerView;
  // const translateX = Math.min(currentIndex * cardWidth.current, totalScrollable);
  // const translateX = Math.max(
  //   0,
  //   currentIndex * cardWidth.current - ((cardsPerView - 1) * cardWidth.current) / 2
  // );
  const translateX = Math.min(
    currentIndex * cardWidth.current,
    totalScrollable
  );

  return (
    <div
      className={`${
        isDesktopModeOnMobile && width > 768 
          ? ""
          : "lg:min-h-screen "
      } w-full flex  justify-center 2xl:items-center lg:pt-[8rem] pt-[6rem] sm:py-[8rem]  px-4 sm:px-6 md:px-6 max-sm:pb-0 pb-10 lg:pb-[4.5rem]`}
    >
      <div className="flex flex-col gap-6 sm:gap-8 text-white w-full max-w-screen-md md:max-w-[90vw] lg:max-w-[80vw] xl:max-w-[80vw] 2xl:max-w-[70vw]">
        <div className="flex flex-col gap-4 sm:gap-6 items-center text-center">
          <h1 className="poppins-semibold text-2xl sm:text-3xl md:text-4xl">
            What we do
          </h1>
        </div>

        <div className="relative" {...swipeHandlers}>
          <div className="overflow-hidden space-x-4">
            <div
              ref={containerRef}
              className="flex space-x-6 h-[30rem] px-4 items-center transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${translateX}px)` }}
            >
              {cards.map((card, index) => (
                <Card
                  key={index}
                  {...card}
                  index={index}
                  cardsPerView={cardsPerView}
                  startIndex={currentIndex}
                  setIsHovered={setIsHovered}
                />
              ))}
            </div>
          </div>

          <div className="hidden lg:flex absolute justify-end gap-4 mt-8 right-2 w-full transform -translate-y-1/2 z-10 px-4">
            <button
              className="bg-[#D9D9D922] w-10 h-10 rounded-full flex items-center justify-center transition duration-300 hover:bg-white/30 hover:scale-110 disabled:opacity-50"
              onClick={handlePrev}
              disabled={currentIndex === 0}
            >
              <img src={LeftSlider} alt="prev" className="w-5 h-5 opacity-70" />
            </button>
            <button
              className="bg-[#D9D9D922] w-10 h-10 rounded-full flex items-center justify-center transition duration-300 hover:bg-white/30 hover:scale-110 disabled:opacity-50"
              onClick={handleNext}
              disabled={currentIndex === maxIndex}
            >
              <img
                src={RightSlider}
                alt="next"
                className="w-5 h-5 opacity-70"
              />
            </button>
          </div>

          <div className="flex lg:hidden justify-center mt-6 space-x-2">
            {Array.from({ length: maxIndex + 1 }).map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentIndex(i)}
                className={`h-2 w-2 rounded-full transition-all duration-300 cursor-pointer ${
                  i === currentIndex
                    ? "w-5 h-3 bg-white"
                    : "w-2 h-2 bg-[#8274b2]"
                } transition-all`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Service;
